// EmbeddedCheckoutModal (BuyLicensesModal)
// -----------------------------------------
// Renders Stripe's EMBEDDED Checkout inside a modal (ui_mode: embedded,
// redirect_on_completion: never — the BE creates the session that way). The
// publishable key + client secret come from the createEntitlementCheckoutSession
// mutation, so Stripe.js is initialised per-session with the tenant's key.
//
// On payment completion Stripe fires onComplete (no redirect). Entitlements are
// provisioned asynchronously by the Stripe webhook, so we show a "received —
// appearing shortly" state and let the caller refresh the license list.
//
// Used for both flows that mount Stripe's embedded UI — buying licenses
// (mode: payment/subscription) and replacing the card on file (mode: setup) —
// because the mounting, teardown and completion handling are identical and the
// only difference is copy. The copy props default to the buy-licenses wording,
// so existing callers are unaffected.

import React, { useCallback, useMemo, useState } from "react";
// The "/pure" entry point, NOT the package root. Importing "@stripe/stripe-js"
// injects the js.stripe.com script as a side effect of the import itself:
//
//   Promise.resolve().then(function () { return getStripePromise(); })
//
// This module is reached from the Billing page, which the single un-split app
// bundle pulls in on boot — so the plain import fetched Stripe.js, and ran
// Stripe's fraud telemetry, on EVERY page of the dashboard, for every user,
// including those with no route to billing at all. "/pure" is Stripe's own
// side-effect-free build: the script is fetched only when loadStripe() is
// actually called, i.e. when this checkout modal opens.
//
// The documented cost is that Stripe's fraud signals no longer span pages
// outside checkout. That coverage buys nothing here — the modal below is the
// only place in the product that transacts.
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BuyLicensesModalProps {
  clientSecret: string;
  publishableKey: string;
  // Dismiss the modal (may be before completion — the user can cancel).
  onClose: () => void;
  // Payment finished; the webhook will provision. Caller refreshes licenses.
  onComplete: () => void;
  // Copy overrides for flows other than buying licenses.
  ariaLabel?: string;
  completedTitle?: string;
  completedMessage?: string;
}

export default function BuyLicensesModal({
  clientSecret,
  publishableKey,
  onClose,
  onComplete,
  ariaLabel = "Buy app licenses",
  completedTitle = "Payment received",
  completedMessage = "Your new app licenses will appear below shortly.",
}: BuyLicensesModalProps) {
  const [completed, setCompleted] = useState(false);

  // loadStripe returns a promise; memoise per key so it isn't recreated each render.
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);

  const handleComplete = useCallback(() => {
    setCompleted(true);
    onComplete();
  }, [onComplete]);

  const options = useMemo(
    () => ({ clientSecret, onComplete: handleComplete }),
    [clientSecret, handleComplete]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        {completed ? (
          <div className="p-8 text-center">
            <p className="text-lg font-semibold text-gray-900">{completedTitle}</p>
            <p className="mt-2 text-sm text-gray-500">{completedMessage}</p>
            <Button onClick={onClose} className="mt-6">
              Done
            </Button>
          </div>
        ) : (
          <div className="p-2 sm:p-4">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}
      </div>
    </div>
  );
}
